import React from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { ISubscriptionPlan } from "../../interface/pricing";
import { REIAdvanced, REIProfessional, REIStarter } from "../../public/assets/images/pricing";

interface PricingCardProps {
  plan: ISubscriptionPlan;
  isAnnual: boolean;
  discountPercent: number;
  onSelect: () => void;
  isPopular?: boolean;
}

const getPlanLogo = (planName: string) => {
  if (planName.toLowerCase().includes("starter")) return REIStarter;
  if (planName.toLowerCase().includes("professional")) return REIProfessional;
  if (planName.toLowerCase().includes("advanced")) return REIAdvanced;
  return REIStarter;
};

const PricingCard: React.FC<PricingCardProps> = ({ plan, isAnnual, discountPercent, onSelect, isPopular = false }) => {
  return (
    <Card
      className={`pricing-card relative ${
        isPopular ? "border-4 border-blue-500 shadow-2xl" : "border border-gray-200 shadow-lg"
      } hover:shadow-2xl transition-shadow duration-300`}
    >
      {isPopular && (
        <div className="absolute top-4 right-4 p-3">
          <Badge value="MOST POPULAR" severity="success" className="text-sm content-center align-middle" />
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-3xl font-bold text-gray-900 mb-2">{plan.planName}</h3>
      </div>

      <div className="text-center mb-6">
        <div className="text-gray-400 line-through text-lg mb-1">
          Was ${plan.originalPrice}
          {isAnnual ? "/year" : "/month"}
        </div>

        <div className="text-5xl font-bold text-gray-900 mb-2">
          Now ${plan.displayPrice}
          <span className="text-2xl font-normal text-gray-600">{isAnnual ? "/year" : "/month"}</span>
        </div>

        {isAnnual && plan.annualPrice.totalSavings > 0 && (
          <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
            Save ${plan.annualPrice.totalSavings} {isAnnual && `(${discountPercent}% off)`}
          </div>
        )}
      </div>

      <div className="mb-6">
        <ul className="space-y-3">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <Button
        id={`pcard-btn-signup-${plan.planId}`}
        data-testid={`pcard-btn-signup-${plan.planId}`}
        label="SIGN UP"
        onClick={onSelect}
        className={`w-full py-3 text-lg font-semibold ${isPopular ? "p-button-success" : "p-button-primary"}`}
      />

      <p className="text-sm text-gray-500 text-center mt-4">{plan.description}</p>
      <div className="text-center">
        <img src={getPlanLogo(plan.planName)} alt={`${plan.planName} Logo`} className="w-auto h-auto  mt-4 " />
      </div>
    </Card>
  );
};

export default PricingCard;
